import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
} from 'react';
import { LocationType } from 'types';
import { useBranches } from 'hooks/useBranches';
import { getBranchLocation, setBranchLocation } from 'utils/localStorage';
import { useSearchParams } from 'react-router-dom';

/** =============================
 *  Types & Constants
 *  =============================
 */
interface BranchContextType {
  allBranches: LocationType[] | null;
  kioskLocation: LocationType | null;
  isLoading: boolean;
  error: string | unknown;
}

const STORAGE_KEY = 'allBranches';
const DEFAULT_LOCATION_CODE = 'da3';

/** =============================
 *  Context
 *  =============================
 */
export const BranchesContext = createContext<BranchContextType | undefined>(
  undefined
);

/** =============================
 *  Provider Component
 *  =============================
 */
export const BranchesContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  /**
   * Load branches from localStorage on initial render
   */
  const [allBranches, setAllBranches] = useState<LocationType[] | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      console.warn('Failed to parse branches from localStorage');
      return null;
    }
  });

  /**
   * Fetch branches from API only if not present in localStorage
   */
  const { data: branches, isLoading, error } = useBranches(!allBranches);

  /**
   * Persist branches to localStorage when fetched
   */
  useEffect(() => {
    if (branches) {
      setAllBranches(branches);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(branches));
    }
  }, [branches]);

  /**
   * Helper to find a branch by code
   */
  const getBranchByCode = (code?: string | null): LocationType | null => {
    if (!code || !allBranches) return null;
    return (
      allBranches.find(
        (branch) => branch.Code.toLowerCase() === code.toLowerCase()
      ) || null
    );
  };

  /**
   * Read query param from URL for locationCode
   */

  const queryParams = new URLSearchParams(window.location.search);
  const locationCode = queryParams.get('locationCode');

  /**
   * Determine initial kiosk location
   * Priority:
   *   1. Saved in localStorage
   *   2. URL param
   *   3. Default code
   */
  const initialKioskLocation = useMemo(() => {
    if (locationCode) {
      return getBranchByCode(locationCode);
    }
    return getBranchByCode(DEFAULT_LOCATION_CODE); // Default branch if no URL param
  }, [allBranches, locationCode]);

  /**
   * Store kioskLocation state
   */
  const [kioskLocation, setKioskLocation] = useState<LocationType | null>(
    initialKioskLocation || null
  );

  // 2. Sync kiosk location when branches or URL param change
  useEffect(() => {
    if (!allBranches) return;

    // If URL param exists → use it
    if (locationCode) {
      const branchFromUrl = getBranchByCode(locationCode);
      if (branchFromUrl && branchFromUrl.Code !== kioskLocation?.Code) {
        setKioskLocation(branchFromUrl);
        setBranchLocation(branchFromUrl); // Persist
      }
      return;
    }

    // If URL param is removed → fall back to default branch
    const defaultBranch = getBranchByCode(DEFAULT_LOCATION_CODE);
    if (defaultBranch && defaultBranch.Code !== kioskLocation?.Code) {
      setKioskLocation(defaultBranch);
      setBranchLocation(defaultBranch); // Persist
    }
  }, [locationCode, allBranches]);

  /**
   * Persist kioskLocation whenever it changes
   */
  useEffect(() => {
    if (kioskLocation) {
      setBranchLocation(kioskLocation);
    }
  }, [kioskLocation]);

  /**
   * Context value
   */
  const value: BranchContextType = {
    allBranches,
    kioskLocation,
    isLoading,
    error,
  };

  return (
    <BranchesContext.Provider value={value}>
      {children}
    </BranchesContext.Provider>
  );
};

/** =============================
 *  Custom Hook
 *  =============================
 */
export function useBranchContext() {
  const context = useContext(BranchesContext);
  if (!context) {
    throw new Error(
      'useBranchContext must be used within a BranchesContextProvider'
    );
  }
  return context;
}
